import React, { useRef, useCallback, PropsWithChildren } from 'react'

import {
  DragDropUploadProvider,
  useDragDropUploadContext
} from 'context/DragDropUpload'
import { TUploadType } from 'consts/uploadTypes'
import { useFileFormatValidator } from 'hooks/useFileFormatValidator'
import * as Styled from './styled'

interface IProps extends Required<PropsWithChildren> {
  filesHandler: (files: File[]) => void
  style?: React.CSSProperties
  wrapperRef?: HTMLDivElement | null
  multiple?: boolean
  acceptType?: TUploadType
  isAreaDraggable?: boolean
  canResizeDuringDrag?: boolean
  onMouseEnter?: () => void
  onClick?: () => void
  showWarning?: (value: boolean) => void
}

const DragDropUploadInner: React.FC<IProps> = ({
  children,
  filesHandler,
  style,
  wrapperRef,
  multiple = true,
  acceptType = 'visualMedia',
  isAreaDraggable = true,
  canResizeDuringDrag = true,
  onMouseEnter,
  onClick,
  showWarning
}) => {
  const dragContentWrapper = useRef<HTMLDivElement | null>(null)
  const dragCounter = useRef(0)
  const { dragDropUploadData, setDragDropUploadData } =
    useDragDropUploadContext()
  const { isDragActive } = dragDropUploadData

  const validateFiles = useFileFormatValidator(acceptType)

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      if (!isAreaDraggable) return
      event.preventDefault()
      event.stopPropagation()

      const { files } = event.dataTransfer
      const fileArray = Array.from(files)

      const validFiles = validateFiles(fileArray)
      const finalFiles = multiple ? validFiles : validFiles.slice(0, 1)

      if (finalFiles.length > 0) {
        filesHandler(finalFiles)
      } else {
        showWarning?.(true)
      }

      dragCounter.current = 0
      changeDragContentWrapperSizes('auto', 'auto')
      setDragDropUploadData({ isDragActive: false })
      wrapperRef?.scrollTo(0, 0)
    },
    // TODO: recheck this
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      multiple,
      filesHandler,
      wrapperRef,
      isAreaDraggable,
      validateFiles,
      showWarning
    ]
  )

  const changeDragContentWrapperSizes = (width: string, height: string) => {
    if (dragContentWrapper.current && wrapperRef && canResizeDuringDrag) {
      dragContentWrapper.current.style.width = width
      dragContentWrapper.current.style.height = height
    }
  }

  const handleDropEnter = (event: React.DragEvent<HTMLDivElement>) => {
    if (!isAreaDraggable) return
    showWarning?.(false)

    event.preventDefault()
    event.stopPropagation()

    dragCounter.current++

    if (dragCounter.current === 1 && !isDragActive) {
      changeDragContentWrapperSizes((style?.width as string) || '100%', '100%')
      setDragDropUploadData({ isDragActive: true })
    }
  }

  const handleDropLeave = (event: React.DragEvent<HTMLDivElement>) => {
    if (!isAreaDraggable) return

    event.preventDefault()
    event.stopPropagation()

    dragCounter.current--

    if (dragCounter.current === 0 && isDragActive) {
      changeDragContentWrapperSizes('auto', 'auto')
      setDragDropUploadData({ isDragActive: false })
    }
  }

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (!isAreaDraggable) return
    event.preventDefault()
    event.stopPropagation()
  }

  const handleDragStart = (event: React.DragEvent<HTMLDivElement>) => {
    if (!isAreaDraggable) return
    if ('id' in event.target) {
      event.dataTransfer.setData('text/plain', event.target.id as string)
    }
  }

  return (
    <Styled.DragDropWrapper
      style={style}
      ref={dragContentWrapper}
      $isDragActive={isDragActive}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
      onDragEnter={handleDropEnter}
      onDragLeave={handleDropLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onDragStart={handleDragStart}
    >
      {children}
    </Styled.DragDropWrapper>
  )
}

const DragDropUpload: React.FC<IProps> = props => {
  return (
    <DragDropUploadProvider>
      <DragDropUploadInner {...props} />
    </DragDropUploadProvider>
  )
}

export default DragDropUpload
